// LISTA UNUI MENIU «⋯» (meniul soluției din vizualizator, meniul rândului pe
// pagina soluțiilor): un `role="menu"` adevărat, nu doar cu numele.
//
//   * la deschidere, focusul trece pe prima opțiune (`role="menuitem"`);
//   * ↑ ↓ Home End trec de la o opțiune la alta, Tab iese și închide;
//   * Escape închide și întoarce focusul pe butonul care l-a deschis;
//   * un clic în afara meniului îl închide — și ieșirea mouse-ului, ca înainte.
//
// Butonul care deschide stă lângă listă, în același părinte: un clic pe el nu
// e «în afară», altfel meniul s-ar închide și s-ar redeschide din același clic.

import { useEffect, useRef, type HTMLAttributes, type KeyboardEvent, type ReactNode } from "react";

export default function MeniuLista({ laInchide, children, className = "", ...rest }: {
  laInchide: () => void;
  children: ReactNode;
  className?: string;
} & Omit<HTMLAttributes<HTMLSpanElement>, "role" | "className" | "children">) {
  const lista = useRef<HTMLSpanElement>(null);
  // ținut într-un ref: părintele dă o funcție nouă la fiecare desenare, iar
  // efectul trebuie să ruleze o singură dată, la deschidere
  const inchide = useRef(laInchide);
  inchide.current = laInchide;

  const optiuni = () => [...(lista.current?.querySelectorAll<HTMLElement>(
    '[role="menuitem"]:not([disabled])') ?? [])];

  useEffect(() => {
    optiuni()[0]?.focus();
    const afara = (e: MouseEvent) => {
      const gazda = lista.current?.parentElement ?? lista.current;
      if (gazda && !gazda.contains(e.target as Node)) inchide.current();
    };
    document.addEventListener("mousedown", afara);
    return () => document.removeEventListener("mousedown", afara);
  }, []);

  const taste = (e: KeyboardEvent<HTMLSpanElement>) => {
    const toate = optiuni();
    const i = toate.indexOf(document.activeElement as HTMLElement);
    const mergi = (j: number) => {
      e.preventDefault();
      toate[(j + toate.length) % toate.length]?.focus();
    };
    switch (e.key) {
      case "ArrowDown": return mergi(i + 1);
      case "ArrowUp": return mergi(i < 0 ? toate.length - 1 : i - 1);
      case "Home": return mergi(0);
      case "End": return mergi(toate.length - 1);
      case "Escape": {
        e.preventDefault();
        e.stopPropagation();
        const buton = lista.current?.parentElement
          ?.querySelector<HTMLElement>('[aria-haspopup="menu"]');
        inchide.current();
        buton?.focus();
        return;
      }
      case "Tab":
        inchide.current();
        return;
    }
  };

  return (
    <span ref={lista} role="menu" className={`card meniu-lista ${className}`.trim()}
          onKeyDown={taste} {...rest}>
      {children}
    </span>
  );
}
